import type { EditorHintRegistry } from "./types";

/**
 * Registr nápověd editoru inspirace (řez 10, typy v `./types`): u každého pole editoru kde se
 * hodnota ukáže na webu, nebo k čemu slouží v back office; sloupce `activities`, které editor
 * nemá; cíle odkazů „Zobrazit na webu“ sedmi záložek. Bez runtime importů: měřidlo
 * `scripts/kontrola/admin-napovedy.mjs` ho načítá přes `nactiTs`, proto kotvy záložek stojí jako
 * literály (měřidlo je ověřuje proti `WEB_SECTIONS.inspiration`).
 *
 * Čtenáři jsou soubory od kořene repa, které hodnotu čtou pod jménem pole, nebo pod jménem
 * projekce API z `readAs`. `included` a `tags` web nečte, ale čte a upravuje je AI asistent
 * adminu, takže zůstávají jako pole back office (spor „čtenář: asistent a projekce API“).
 */
const DETAIL = "apps/web/src/components/pages/InspirationDetail.astro";
const LISTING = "apps/web/src/pages/inspirace/index.astro";
const PUBLIC_API = "apps/api/src/routes/public/activities.ts";
const RELATED = "apps/api/src/lib/relatedInspirations.ts";
const AGENT_CONTEXT = "apps/api/src/lib/agent/context.ts";

export const inspirationHintRegistry: EditorHintRegistry = {
  editor: "inspiration",
  table: "activities",
  variants: ["inspiration"],
  pages: { inspiration: "inspiration" },
  fields: {
    title: {
      kind: "web",
      hintKey: "webHints.inspiration.title",
      readers: [
        DETAIL,
        LISTING,
        "apps/web/src/lib/destinationPlaces.ts",
        "apps/web/src/components/pages/MonthPage.astro",
      ],
    },
    slug: { kind: "web", hintKey: "webHints.inspiration.slug", readers: [DETAIL] },
    activityTypes: {
      kind: "web",
      hintKey: "webHints.inspiration.activityTypes",
      readers: [LISTING, DETAIL, "apps/web/src/components/SearchModal.tsx"],
    },
    difficultyLevel: {
      kind: "web",
      hintKey: "webHints.inspiration.difficultyLevel",
      readers: [LISTING],
    },
    duration: { kind: "web", hintKey: "webHints.inspiration.duration", readers: [LISTING] },
    destinationId: {
      kind: "web",
      hintKey: "webHints.inspiration.destinationId",
      readers: [DETAIL, PUBLIC_API],
      readAs: ["destinationSlug"],
    },
    location: {
      kind: "web",
      hintKey: "webHints.inspiration.location",
      readers: [DETAIL, LISTING],
    },
    heroTitle: { kind: "web", hintKey: "webHints.inspiration.heroTitle", readers: [DETAIL] },
    heading: { kind: "web", hintKey: "webHints.inspiration.heading", readers: [DETAIL] },
    shortDescription: {
      kind: "web",
      hintKey: "webHints.inspiration.shortDescription",
      readers: [DETAIL, LISTING, "apps/web/src/components/pages/MonthPage.astro"],
    },
    metaTitle: { kind: "web", hintKey: "webHints.inspiration.metaTitle", readers: [DETAIL] },
    metaDescription: {
      kind: "web",
      hintKey: "webHints.inspiration.metaDescription",
      readers: [DETAIL],
    },
    featuredImageId: {
      kind: "web",
      hintKey: "webHints.inspiration.featuredImageId",
      readers: [DETAIL, LISTING, PUBLIC_API],
      readAs: ["featuredImageMedia"],
    },
    sortOrder: {
      kind: "backoffice",
      hintKey: "webHints.inspiration.sortOrder",
      readers: [PUBLIC_API, RELATED],
    },
    isFeatured: {
      kind: "backoffice",
      hintKey: "webHints.inspiration.isFeatured",
      readers: [RELATED],
    },
    status: { kind: "backoffice", hintKey: "webHints.inspiration.status", readers: [PUBLIC_API] },
    description: { kind: "web", hintKey: "webHints.inspiration.description", readers: [DETAIL] },
    galleryImageIds: {
      kind: "web",
      hintKey: "webHints.inspiration.galleryImageIds",
      readers: [DETAIL, PUBLIC_API],
      readAs: ["galleryMedia"],
    },
    included: {
      kind: "backoffice",
      hintKey: "webHints.inspiration.included",
      readers: [AGENT_CONTEXT, "apps/api/src/lib/contentEdit.ts"],
    },
    tags: { kind: "backoffice", hintKey: "webHints.inspiration.tags", readers: [AGENT_CONTEXT] },
    socialReels: { kind: "web", hintKey: "webHints.inspiration.socialReels", readers: [DETAIL] },
    videos: { kind: "web", hintKey: "webHints.inspiration.videos", readers: [DETAIL] },
    tips: { kind: "web", hintKey: "webHints.inspiration.tips", readers: [DETAIL] },
    testimonialQuote: {
      kind: "web",
      hintKey: "webHints.inspiration.testimonialQuote",
      readers: [DETAIL],
    },
    testimonialAuthor: {
      kind: "web",
      hintKey: "webHints.inspiration.testimonialAuthor",
      readers: [DETAIL],
    },
    testimonialAuthorImageId: {
      kind: "web",
      hintKey: "webHints.inspiration.testimonialAuthorImageId",
      readers: [DETAIL, PUBLIC_API],
      readAs: ["testimonialAuthorImageMedia"],
    },
    relatedItems: {
      kind: "web",
      hintKey: "webHints.inspiration.relatedItems",
      readers: [DETAIL, PUBLIC_API],
      readAs: ["relatedActivities"],
    },
    // Karta mimo formulář: vazby zájezd ↔ inspirace (`InspirationTripsCard` na záložce
    // Související) se ukládají hned podzdrojem, ne formulářem.
    linkedTrips: {
      kind: "web",
      hintKey: "webHints.inspiration.linkedTrips",
      readers: ["apps/web/src/lib/inspirationTripGroups.ts", PUBLIC_API],
      outsideForm: true,
    },
  },
  columnsOutsideEditor: {
    id: { status: "system", reason: "identita záznamu" },
    createdAt: { status: "system", reason: "časová značka založení" },
    updatedAt: { status: "system", reason: "časová značka poslední změny" },
    featuredImage: {
      status: "system",
      reason:
        "legacy URL; web ho čte jen jako zálohu za médiem, plní ho migrace médií, editor edituje featuredImageId",
    },
    galleryImages: {
      status: "system",
      reason:
        "legacy URL; web ho čte jen jako zálohu za médiem, plní ho migrace médií, editor edituje galleryImageIds",
    },
    testimonialAuthorImage: {
      status: "system",
      reason:
        "legacy URL; web ho čte jen jako zálohu za médiem, plní ho migrace médií, editor edituje testimonialAuthorImageId",
    },
    activityType: {
      status: "system",
      reason:
        "první typ ze seznamu Typy aktivity v pořadí výčtu; zapisuje ho admin API při uložení, nic ho nečte (F4.1)",
    },
    priceFrom: {
      status: "necte",
      reason: "inspirace se neprodává (vize G1), cenu inspirace nečte web ani nic jiného",
    },
    tipsContent: {
      status: "bezPolicka",
      reason:
        "starý text tipů bez políčka; web ho nečte, prohledává ho jen fulltext AI asistenta adminu; data zůstávají",
    },
  },
  tabs: {
    basic: { inspiration: { anchor: "popis" } },
    description: { inspiration: { anchor: "popis" } },
    gallery: { inspiration: { anchor: "fotogalerie" } },
    included: { inspiration: { backoffice: "webHints.inspiration.tabIncluded" } },
    videos: { inspiration: { anchor: "videa" } },
    tips: { inspiration: { anchor: "tipy" } },
    related: { inspiration: { anchor: "inspirace" } },
  },
};
